import {CommandBase} from "./commandBase";
import {RemoteConstantsModel} from "../models/remoteConstants.model";
import {identityOf, MigratableRecord, RecordMigration} from "../utils/migrateRecord";
import {ensureSectionCollection} from "./sectionCollections";

// Inserts a record migrated from Other into Current — metadata or
// entity/techTree, the package shape is the same. Bundles the record itself
// plus its satellite data (localization rows, image pack, new image rows)
// so one undo reverts the whole arrival.
//
// All writes are additive — entries that already exist on Current are left
// alone, on the assumption that Current's edits are intentional. The
// classifier upstream has already verified the record's identity isn't in
// use on Current; satellites just check for presence before writing.
export class MigrateRecordCommand extends CommandBase {
    migration: RecordMigration;

    constructor(migration: RecordMigration) {
        super();
        this.migration = migration;
    }

    execute(data: RemoteConstantsModel): boolean {
        const collection = ensureSectionCollection(data, this.migration.sectionKey);
        if (!collection) return false;
        if (collection.some(r => identityOf(r, this.migration.sectionKey) === this.migration.identity)) {
            return false; // identity already in use (race with classifier — refuse)
        }

        // Deep-clone the record so subsequent edits in Current don't accidentally
        // mutate Other's in-memory state.
        const cloned: MigratableRecord = JSON.parse(JSON.stringify(this.migration.record));
        collection.push(cloned);

        // Localization: add entries that don't already exist on Current.
        if (!data.localization) data.localization = {};
        for (const row of this.migration.locRows) {
            if (!data.localization[row.lang]) data.localization[row.lang] = {};
            if (data.localization[row.lang][row.key] === undefined) {
                data.localization[row.lang][row.key] = row.value;
            }
        }

        // Image pack at the record's identity. Always safe to write because
        // the identity is brand new to Current (verified by the classifier).
        if (this.migration.packRow) {
            if (!data.imagePacks) data.imagePacks = {};
            data.imagePacks[this.migration.identity] = JSON.parse(JSON.stringify(this.migration.packRow));
        }

        // Image metadata rows: only those Current didn't already have.
        if (!data.images) data.images = {};
        for (const row of this.migration.imageRows) {
            if (data.images[row.key] === undefined) {
                data.images[row.key] = JSON.parse(JSON.stringify(row.value));
            }
        }

        return true;
    }
}
